import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Eye, Lock, ShieldCheck } from 'lucide-react';
import { sanitizeRouteParam } from '@/utils';
import { triggerDownload } from '@/utils/download';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import { FileBrowser } from '@/components/files';
import { countTreeFiles } from '@/components/files/tree';
import publicShareService, {
  clearShareSession,
  getShareSession,
} from '@/services/shared-storage/publicShareService';

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : null;

/**
 * The page a recipient lands on from their invite link.
 *
 * The link identifies who they are; the PIN proves it is really them. The PIN
 * is deliberately not in the invite email — the firm passes it on separately.
 */
const SharedSpaceView = () => {
  const { token: rawToken } = useParams();
  const token = sanitizeRouteParam(rawToken);

  const [summary, setSummary] = useState(null);
  const [spaceName, setSpaceName] = useState(null);
  const [docCount, setDocCount] = useState(null);
  // False for a View Only link (OL-166); null until the tree says which.
  const [canDownload, setCanDownload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deadLink, setDeadLink] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pinError, setPinError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await publicShareService.getSummary(token);
        if (cancelled) return;
        setSummary(data);
        // A session survives a refresh within the same tab.
        setUnlocked(!!getShareSession(token));
      } catch (error) {
        if (cancelled) return;
        setDeadLink(getApiErrorMessage(error, 'This link is no longer available.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleUnlock = async (event) => {
    event.preventDefault();
    if (pin.length !== 6) {
      setPinError('The PIN is 6 digits.');
      return;
    }
    setSubmitting(true);
    setPinError(null);
    try {
      await publicShareService.unlock(token, pin);
      setUnlocked(true);
      setPin('');
    } catch (error) {
      const status = error?.response?.status;
      setPinError(
        status === 429
          ? 'Too many incorrect attempts. Try again in a little while.'
          : getApiErrorMessage(error, 'That PIN is not correct.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  /** Session expiry sends the recipient back to the PIN screen, not to a login page. */
  const handleSessionLoss = useCallback(() => {
    clearShareSession(token);
    setUnlocked(false);
    toast.info('Please enter your PIN again.', {
      description: 'Sessions end after a period of inactivity to keep these documents secure.',
    });
  }, [token]);

  /* Memoized — FileBrowser reloads whenever this identity changes. */
  const api = useMemo(
    () => {
      const guard = async (fn) => {
        try {
          return await fn();
        } catch (error) {
          if (error?.response?.status === 401) handleSessionLoss();
          throw error;
        }
      };

      return {
        // The space name only comes back once the PIN has been accepted — it can
        // give away what the documents are, so it is not in the pre-PIN summary.
        getTree: () =>
          guard(async () => {
            const payload = await publicShareService.getTree(token);
            setSpaceName(payload.space_name ?? null);
            setDocCount(countTreeFiles(payload.nodes ?? []));
            setCanDownload(payload.can_download !== false);
            return payload.nodes ?? [];
          }),
        getPreview: (fileId) => guard(() => publicShareService.getPreview(token, fileId)),
        getPdf: (fileId) => guard(() => publicShareService.getPdf(token, fileId)),
        getDownloadUrl: (fileId) => guard(() => publicShareService.getDownloadUrl(token, fileId)),
        downloadFolderZip: (folderId) =>
          guard(() => publicShareService.downloadFolderZip(token, folderId)),
      };
    },
    [token, handleSessionLoss],
  );

  // One busy flag for every zip control — 'all' for the header button, a node
  // id for a folder row. While any zip streams, all of them are disabled.
  const [zippingId, setZippingId] = useState(null);

  const downloadZip = async (folderId, name) => {
    if (zippingId) return;
    setZippingId(folderId ?? 'all');
    try {
      const blob = await api.downloadFolderZip(folderId);
      triggerDownload(blob, `${name}.zip`);
    } catch (error) {
      // 401 means the session ended — the guard already sent them to the PIN screen.
      if (error?.response?.status !== 401) {
        toast.error('Download failed.', {
          description: getApiErrorMessage(error, 'Please try again.'),
        });
      }
    } finally {
      setZippingId(null);
    }
  };

  if (loading) {
    return <p className="text-center text-2sm text-gray-600 py-10">Loading…</p>;
  }

  if (deadLink) {
    return (
      <div className="card w-full max-w-md mx-auto">
        <div className="card-body text-center py-10">
          <Lock size={28} className="mx-auto text-gray-400 mb-3" />
          {/* Short heading, with the server's wording as the body — the two
              would otherwise read as the same sentence twice. */}
          <h2 className="text-lg font-medium text-gray-900">Access unavailable</h2>
          <p className="text-2sm text-gray-600 mt-2">{deadLink}</p>
          <p className="text-2sm text-gray-600 mt-3">
            It may have expired or been withdrawn. Contact the firm that sent it to you.
          </p>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="card w-full max-w-md mx-auto">
        <div className="card-body py-8">
          <div className="text-center mb-5">
            <ShieldCheck size={28} className="mx-auto text-primary mb-3" />
            <h2 className="text-lg font-medium text-gray-900">Secure documents</h2>
            <p className="text-2sm text-gray-600 mt-1">
              {summary?.recipient_name ? `Hello ${summary.recipient_name}. ` : ''}
              Enter the PIN you were given to open these documents.
            </p>
          </div>

          <form onSubmit={handleUnlock} className="flex flex-col gap-3">
            <div>
              <label className="form-label">PIN</label>
              <input
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value.replace(/\D/g, ''));
                  setPinError(null);
                }}
                className="input w-full text-center text-xl tracking-[0.5em] font-mono"
                placeholder="••••••"
              />
              {pinError && <p className="text-danger text-2xs mt-1">{pinError}</p>}
            </div>

            <button type="submit" className="btn btn-primary w-full" disabled={submitting || pin.length !== 6}>
              {submitting ? 'Checking…' : 'Open documents'}
            </button>
          </form>

          <p className="text-2xs text-gray-500 mt-4 text-center">
            The PIN was not sent in your invite email. If you do not have it, contact the firm directly.
          </p>

          {summary?.expires_at && (
            <p className="text-2xs text-gray-500 mt-2 text-center">
              Access to these documents ends on {fmtDate(summary.expires_at)}.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card w-full max-w-4xl mx-auto">
      <div className="card-header flex-wrap gap-2">
        <h3 className="card-title">{spaceName ?? 'Shared documents'}</h3>
        <div className="flex items-center gap-3">
          {docCount != null && (
            <span className="badge badge-pill badge-outline">
              {docCount} {docCount === 1 ? 'document' : 'documents'}
            </span>
          )}
          {summary?.expires_at && (
            <span className="text-2xs text-gray-500">Access ends {fmtDate(summary.expires_at)}</span>
          )}
          {canDownload === true && (
            <button
              className="btn btn-sm btn-light"
              onClick={() => downloadZip(null, spaceName ?? 'shared-documents')}
              disabled={zippingId !== null}
            >
              <Download size={14} />
              {zippingId === 'all' ? 'Preparing…' : 'Download all'}
            </button>
          )}
          {canDownload === false && (
            <span className="badge badge-pill badge-outline badge-primary">
              <Eye size={12} /> View only
            </span>
          )}
        </div>
      </div>
      <div className="card-body">
        <FileBrowser
          fileableType="shared_space"
          fileableId={token}
          readOnly
          canDownload={canDownload !== false}
          api={api}
          onDownloadFolder={canDownload === true ? (node) => downloadZip(node.id, node.name) : undefined}
          downloadFolderBusy={zippingId !== null}
        />
        <p className="text-2xs text-gray-500 mt-3">
          {canDownload === false
            ? 'You can watch videos and read PDFs and images here. Downloading is turned off for this link, and other file types can’t be opened. Every document you open is recorded. Please do not forward this link.'
            : 'Every document you open or download is recorded. Please do not forward this link.'}
        </p>
      </div>
    </div>
  );
};

export { SharedSpaceView };
